import {
  ACCOUNT_MAPPING_CTAS,
  INTEGRATIONS_CTAS,
  PARTNER_CTAS,
  PLAN_AND_BILLING_CTAS,
  POPULATION_CTAS,
  SHARED_LIST_CTAS,
  TEAM_CTAS,
} from '@crossbeam/itly';

import {
  ALL_PARTNERS_TYPE,
  CUSTOM_TYPE,
  GREENFIELD_TYPE,
  PARTNER_TAGS_TYPE,
  POTENTIAL_REVENUE_TYPE,
} from '@/constants/reports';
import { CO_SELLER, PARTNER_MANAGER, VIEWER } from '@/constants/team'; // TODO: Migrate SE Roles
import type { BillingTier } from '@/types/billing';

export const BILLING_VERSION = 4;

export const BILLING_TIERS = {
  SUPERNODE: 'enterprise',
  FREE: 'free',
  CONNECTOR: 'connector',
  ENTERPRISE: 'enterprise_july_2025',
} as const;

// PPV5 ONLY
export const TIER_TO_READABLE_NAME: Record<string, string> = {
  [BILLING_TIERS.SUPERNODE]: 'Supernode',
  [BILLING_TIERS.FREE]: 'Free',
  [BILLING_TIERS.CONNECTOR]: 'Connector',
  [BILLING_TIERS.ENTERPRISE]: 'Enterprise',
};

export const FULL_ACCESS = 'full_access';
export const SALES_ONLY = 'sales_only';
export const CONNECTOR_SEATS = 5;
export const EXPLORER_SEATS = 3;
export const EXPLORER_OFFLINE_PARTNER_LIMIT = 1;
export const PAID_OFFLINE_PARTNER_LIMIT = 50;
/* Mirrors muncher's virtual-partners-quotas--expanded; display copy only, not
   an org's entitlement (billingInfo.core.virtual_partners_quota). */
export const ODP_LIMIT_DEFAULTS_BY_TIER: Record<BillingTier, number> = {
  free: 1,
  connector: 1,
  enterprise: 3,
  enterprise_july_2025: 10,
};

export const BILLING_PLAN_MONTHLY = 'monthly';
export const BILLING_PLAN_ANNUAL = 'annual';
export function normalizeBillingPeriod(period: string) {
  if (['annual', 'annually', 'year', 'yearly'].includes(period))
    return BILLING_PLAN_ANNUAL;
  return BILLING_PLAN_MONTHLY;
}

/* Record export limit */
export const OVER_75 = 'over-75';
export const OVER_90 = 'over-90';
export const LIMIT_REACHED = 'limit-reached';

export const CREDIT_LIMIT_WARNING_THRESHOLD = 0.9;
export const CREDIT_LIMIT_DANGER_THRESHOLD = 1;

export const CREDIT_LIMIT_DEFAULTS_BY_TIER: Record<BillingTier, number> = {
  free: 50,
  connector: 500,
  enterprise: 2500,
  enterprise_july_2025: 5000,
};

export const USAGE_LIMIT_WARNING = 'usage-limit-warning';
export const USAGE_LIMIT_DANGER = 'usage-limit-danger';

/* Chargify components */
export const ANNUAL_SUBSCRIPTION = 'connector-annual-subscription';
export const MONTHLY_SUBSCRIPTION = 'connector-monthly-subscription';
export const ANNUAL_SUBSCRIPTION_V5 = 'connector-annual-subscription-july-2025';
export const MONTHLY_SUBSCRIPTION_V5 =
  'connector-monthly-subscription-july-2025';
export const ANNUAL_SEATS = 'connector-annual-seats';
export const MONTHLY_SEATS = 'connector-monthly-seats';
export const ANNUAL_SALES_SEATS = 'connector-annual-sales-seat';
export const MONTHLY_SALES_SEATS = 'connector-monthly-sales-seat';

export const BILLING_PRODUCTS: Record<string, string> = {
  [BILLING_PLAN_ANNUAL]: ANNUAL_SUBSCRIPTION,
  [BILLING_PLAN_MONTHLY]: MONTHLY_SUBSCRIPTION,
};
export const BILLING_PRODUCTS_V5: Record<string, string> = {
  [BILLING_PLAN_ANNUAL]: ANNUAL_SUBSCRIPTION_V5,
  [BILLING_PLAN_MONTHLY]: MONTHLY_SUBSCRIPTION_V5,
};

export const CORE_SEAT_COMPONENTS: Record<string, string> = {
  [BILLING_PLAN_ANNUAL]: ANNUAL_SEATS,
  [BILLING_PLAN_MONTHLY]: MONTHLY_SEATS,
} as const;

export const SALES_SEAT_COMPONENTS: Record<string, string> = {
  [BILLING_PLAN_ANNUAL]: ANNUAL_SALES_SEATS,
  [BILLING_PLAN_MONTHLY]: MONTHLY_SALES_SEATS,
} as const;

/* Reference between old verbiage and new (we did not migrate
 * the sales roles on the backend, so we use this to show the right copy) */
export const OLD_ROLES_TO_NEW_MAP = {
  [PARTNER_MANAGER]: 'Manager',
  [CO_SELLER]: 'Standard',
  [VIEWER]: 'Limited',
};

/* Used to show the cost of seats during checkout */
export const SEAT_COSTS = {
  [FULL_ACCESS]: {
    month: 16500,
    year: 180000,
  },
  [SALES_ONLY]: {
    month: 2750,
    year: 30000,
  },
};

export const CHARGIFY_TARGET_NODE = 'chargify-form';

/* Account Statuses (e.g. dunning, etc) */
export const PAST_DUE = 'past_due';
export const CANCELLED = 'cancelled';
export const UNPAID = 'unpaid';
export const ACTIVE = 'active';
export const CONNECTOR_TRIAL = 'connector_trial';
export const TRIAL_ENDED = 'trial_ended';

export const ACCOUNT_STATUS_MAP: Record<
  string,
  {
    text: string;
    color: string;
  }
> = {
  [PAST_DUE]: {
    text: 'Past Due',
    color: 'danger',
  },
  [UNPAID]: {
    text: 'Unpaid',
    color: 'danger',
  },
  [CANCELLED]: {
    text: 'Cancelled',
    color: 'warning',
  },
  [ACTIVE]: {
    text: 'Active',
    color: 'success',
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BillingCopyOriginal = Record<string, any>;
export const V4_BILLING_COPY: BillingCopyOriginal = {
  connectorUpgrade: {
    commitmentTitle: 'This plan is an annual commitment',
    commitmentDescription:
      'Your plan will start on %s and renew on %s. If you choose to cancel you will have access through the remainder of the subscription with no partial refunds given.',
    fullAccessSeats: {
      type: FULL_ACCESS,
      title: 'Full Access Seats',
      description:
        'Build a predictable Ecosystem-Led Growth revenue engine with complete access to Crossbeam and admin controls',
      costPerMonth: 165,
      costPerYear: 1800,
    },
    salesSeats: {
      type: SALES_ONLY,
      title: 'Sales Seats',
      description:
        'Grow pipeline and gain ecosystem insights with access to Crossbeam for Sales features',
      costPerMonth: 300,
      costPerYear: 27.5,
    },
    upgradeCard: {
      description:
        'Activate partner ecosystems and boost team performance with advanced features, plays, and customizations',
      features: [
        {
          text: 'Scale-up your organization with no user minimum',
        },
        {
          text: 'Deeply understand ecosystem potential with %s',
          underline: 'advanced account mapping',
          tooltip:
            'Run reports with many partners and unlock all new report types',
        },
        {
          text: 'Get more mileage out of your ecosystem data with %s annually',
          underline: '5,000 unique record exports',
          tooltip:
            'Unlimited exports until you reach 5,000 unique records exported from reports or integrations',
        },
        {
          text: 'Segment and compare data your way with 3 Custom Populations',
        },
        {
          text: 'Accelerate co-selling activities using Shared Lists',
        },
      ],
      alert: {
        month:
          'You will be charged this amount on the %s of every month until your renewal on %s',
        year: "You're saving %s by paying annually",
      },
      error: {
        title: 'Error calculating cost',
        description:
          'Something went wrong while processing the cost of your subscription. Please try again or contact support if this error persists',
      },
      cta_messages: {
        [PLAN_AND_BILLING_CTAS.BILLING]:
          'Unlock our most powerful features, scale collaboration, and more:',

        /* Team/seat CTAs */
        [TEAM_CTAS.AT_SEAT_LIMIT]:
          'Scale collaboration beyond your team, co-sell with sales reps, and more:',
        [TEAM_CTAS.MORE_SEATS]:
          'Scale collaboration beyond your team, co-sell with sales reps, and more:',
        [TEAM_CTAS.FREE_NEEDS_MORE_SEATS]:
          'Scale collaboration beyond your team, co-sell with sales reps, and more:',
        [TEAM_CTAS.ACCEPT_INVITE_REQUESTS]:
          'Scale collaboration beyond your team, co-sell with sales reps, and more:',

        /* Feature CTAs */
        [PARTNER_CTAS.OFFLINE_PARTNERS]:
          'Unlock unlimited offline partners to compare accounts with anyone and more:',
        [POPULATION_CTAS.CUSTOM_POPULATIONS]:
          'Unlock 3 custom populations to segment data your way and more:',
        [ACCOUNT_MAPPING_CTAS.RECORD_EXPORT_LIMIT]:
          'Unlock more record exports to leverage data your way and more:',
        [INTEGRATIONS_CTAS.INTEGRATIONS]:
          'Unlock 1 additional integration to to optimize and enrich workflows and more:',
        [INTEGRATIONS_CTAS.COPILOT_FOR_SALESFORCE]:
          'Unlock the Copilot for Salesforce for your whole team, enable your sales team, and more:',
        [SHARED_LIST_CTAS.SHARED_LISTS]:
          'Unlock Shared Lists to collaborate in real-time with your team and partners on accounts and more:',
        [ACCOUNT_MAPPING_CTAS['single-partner-oppty']]:
          'Unlock pipeline reporting to uncover your highest ROI opportunities and more:',

        /* Report-Specific CTAs */
        [ACCOUNT_MAPPING_CTAS.SAVED_REPORTS_ACCESS]:
          'Unlock access to saved reports to create resources for you and your team and more:',
        [ACCOUNT_MAPPING_CTAS['single-partner-greenfield']]:
          'Unlock non-overlaps with your partners to find greenfield accounts and more:',
        [ACCOUNT_MAPPING_CTAS['partner-tags']]:
          'Unlock dynamic partner group reporting by tag and more:',
        [ACCOUNT_MAPPING_CTAS.standard]:
          'Unlock a 360-degree landscape report for your partner ecosystem and more:',
        [ACCOUNT_MAPPING_CTAS.custom]:
          'Unlock custom report comparisons to compare data your way and gain access to even more:',
        [ACCOUNT_MAPPING_CTAS.DO_MORE_WITH_REPORTS]:
          'Unlock customizable reports, trigger new overlap Slack notifications, and more:',
      },
    },
    preview: {
      emptyState: {
        title: 'Add seats to your plan',
        description:
          'Once you add seats in the Additional Seats section, you’ll see a billing summary here',
      },
      error: {
        title: 'Error calculating seat costs',
        description:
          'Something went wrong while processing the cost of these seats. Please try again or contact support if this problem persists',
      },
    },
  },
  seatPurchase: {
    alert:
      'Your next invoice will be %s on %s and will continue until your plan renewal',
  },
  purchaseSuccessful: {
    title: "It's good to have you here",
    description:
      "We're excited for you to get even more value out of your ecosystem with Crossbeam. While we process this update consider giving your newly unlocked features a try:",
  },
  seatSummary: {
    outOfSeats: {
      title:
        'Whoops! It looks like you’re using more seats than you have in your contract',
      description:
        "Don't worry, a member of our team will reach out if any action is needed",
    },
    atSeatLimit: {
      title: "You're using all of your available seats",
      description: 'Reach out to our team to add more seats into your contract',
    },
    withinExcess: {
      title: "You're using seats above your purchased amount",
      description:
        'This is authorized under your current agreement. No action needed.',
    },
  },
  outOfSeatsCta: {
    message: "Let's keep a good thing going",
    description:
      "You don't have enough seats for this wonderful group of people. Try adding %s Full Access %s or %s Sales %s",
  },
  gettingValueCta: {
    message: 'Your organization is getting value out of Crossbeam',
    description: 'Unblock your team with more seats and the power of ecosystem',
  },
  explorePlanCards: {
    explorer: {
      price: 'Free',
      subtitle: 'for 3 Full Access seats',
      features: {
        top: [
          { text: '1 offline partner' },
          { text: '3 Standard populations' },
        ],
        partnerships: [
          {
            text: '%s',
            injection: 'Single-Partner Account Mapping',
            tooltip: 'Run single partner reports to view overlapping accounts',
          },
        ],
        sales: [{ text: 'Not available on this plan' }],
        integrations: [{ svg: 'slackIcon', text: 'Slack App' }],
      },
    },
    connector: {
      subtitle: 'per seat/month',
      features: {
        top: [
          { text: 'Unlimited offline partners' },
          { text: '3 Custom Populations' },
          {
            text: '%s',
            injection: '5,000 unique record exports',
            tooltip:
              'Unlimited exports until you reach 5,000 unique records exported from reports or integrations',
          },
        ],
        partnerships: [
          {
            text: '%s',
            injection: 'Multi-Partner Account Mapping',
            tooltip:
              'Run reports with many partners, unlock new report types, save reports, and add new overlap notifications',
          },
          {
            text: 'Compare %s',
            injection: 'non-overlapping accounts',
            tooltip: 'Run greenfield reports',
          },
          {
            text: 'Share %s with partners',
            injection: 'account lists',
            tooltip: 'Create shared lists to collaborate live',
          },
        ],
        sales: [
          {
            text: 'Use %s to unlock ecosystem intel',
            injection: 'Copilot',
            tooltip:
              'Access your partner overlap to identify the best partners to assist with deals',
          },
          {
            text: 'View & add %s',
            injection: 'enriched contacts',
            tooltip:
              'Ecosystem intelligence signals help sellers identify the right contacts to prioritize and personalize outreach to',
          },
          {
            text: 'Access %s',
            injection: 'contextual plays',
            tooltip:
              'Access relevant actions sellers can take based on partner status and contextual insight',
          },
          {
            text: 'Create personal & shared %s',
            injection: 'account lists',
            tooltip:
              'Prioritize strategic accounts for different GTM teams based on overlapping intel',
          },
          {
            text: 'Collaborate & %s with partners',
            injection: 'co-sell',
            tooltip:
              'Leverage Messages to easily gather intel from partner accounts with pre-built and customizable templates and alerts',
          },
        ],
        integrations: [
          { svg: 'chromeIcon', text: 'Copilot for Chrome' },
          { svg: 'slackIcon', text: 'Slack App' },
          { svg: 'salesforceIcon', text: 'Copilot for Salesforce' },
          { svg: 'hubspotIcon', text: 'Copilot for HubSpot' },
          { text: 'All Connector integrations' },
        ],
      },
    },
    supernode: {
      price: 'Custom',
      subtitle: 'for 10 seats minimum',
      features: {
        top: [
          { text: 'Audit Logs & SAML SSO' },
          { text: 'Unlimited Custom Populations' },
          {
            text: '%s',
            injection: 'Custom record exports',
            tooltip:
              'Unlimited exports until you reach the number of unique records negotiated with our team',
          },
          { text: 'Dedicated CSM & support' },
        ],
        partnerships: [
          {
            text: '%s',
            injection: 'Custom roles & permissions',
            tooltip: 'Assign your team custom permission sets',
          },
        ],
        sales: [
          { text: 'Get customized support & enablement' },
          {
            text: 'Access %s',
            injection: 'ecosystem sales reporting',
            tooltip:
              'Leverage our Custom Object integration to power reporting within your CRM',
          },
          {
            text: 'Leverage %s to forecast',
            injection: 'ecosystem data',
            tooltip:
              'Easily see which sales opportunities have partner overlap directly in Clari',
          },
          {
            text: 'Track & measure %s',
            injection: 'deal attribution',
            tooltip:
              'Mark source & influence from your partners right in Crossbeam',
          },
        ],
        integrations: [
          { svg: 'chromeIcon', text: 'Copilot for Chrome' },
          { svg: 'slackIcon', text: 'Slack App' },
          { svg: 'salesforceIcon', text: 'Copilot for Salesforce' },
          { svg: 'salesforceIcon', text: 'Salesforce Custom Object' },
          { svg: 'hubspotIcon', text: 'HubSpot Custom Object' },
          { svg: 'hubspotIcon', text: 'Copilot for HubSpot' },
          { text: 'Unlimited Integrations' },
        ],
      },
    },
  },
};

export const V5_BILLING_COPY: BillingCopyOriginal = {
  ...V4_BILLING_COPY,
  purchaseSuccessful: {
    title: 'Welcome to Connector',
    description:
      'It’s good to have you here. Discover what you’ve unlocked with Connector:',
    descriptionOnboarding:
      'It looks like you haven’t finished your onboarding yet — let’s get you set up',
  },
  seatPurchase: {
    alert: 'Your next invoice will be %s at your scheduled plan renewal on %s',
  },
  explorePlanCards: {
    ...V4_BILLING_COPY.explorePlanCards,
    connector: {
      ...V4_BILLING_COPY.explorePlanCards.connector,
      features: {
        ...V4_BILLING_COPY.explorePlanCards.connector.features,
        integrations: [
          { svg: 'chromeIcon', text: 'Copilot for Chrome' },
          { svg: 'slackIcon', text: 'Slack App' },
          { svg: 'salesforceIcon', text: 'Copilot for Salesforce' },
          { svg: 'hubspotIcon', text: 'Copilot for HubSpot' },
        ],
      },
    },
    supernode: {
      ...V4_BILLING_COPY.explorePlanCards.supernode,
      features: {
        ...V4_BILLING_COPY.explorePlanCards.supernode.features,
        integrations: [
          { svg: 'gong', text: 'Copilot for Gong' },
          { svg: 'outreach', text: 'Copilot for Outreach' },
          { svg: 'salesforceIcon', text: 'Salesforce Custom Object' },
          { text: 'Unlimited Integrations' },
        ],
      },
    },
    enterprise: {
      price: 'Custom',
      features: {
        integrations: [],
      },
    },
  },
};

export type BillingFeature = {
  text: string;
  injection?: string;
  tooltip?: string;
  cta?: string[];
  icon?: string;
  isPlatformFeature?: boolean;
  isBeta?: boolean;
  isMcpServer?: boolean;
  tooltipCreditSuffix?: string;
  newPricingOnly?: boolean;
  hiddenUnderNewPricing?: boolean;
  textNewPricing?: string;
  tooltipNewPricing?: string;
  injectionNewPricing?: string;
};
type BillingCopy = Record<
  string,
  {
    description: string;
    descriptionNewPricing?: string;
    features?: BillingFeature[];
    features_v5: BillingFeature[];
    featuresLongDescription?: BillingFeature[];
  }
>;
export const BILLING_IMPROVEMENTS_COPY: BillingCopy = {
  explorer: {
    description:
      'Get started with building your partner ecosystem and map accounts for free',
    descriptionNewPricing:
      'Build your partner ecosystem and map accounts for free',
    features: [
      {
        text: '%s',
        injection: 'Single-Partner Account Mapping',
        tooltip: 'Run single partner reports to view overlapping accounts',
      },
      { text: '3 Standard Populations' },
      {
        text: '1 %s',
        injection: 'Offline Partner',
        tooltip:
          'Account map with partners that are not active on Crossbeam yet',
      },
    ],
    features_v5: [
      {
        text: '%s up to 50 records at a time',
        injection: 'Single-Partner Account Mapping',
        tooltip:
          'Run single partner reports to view up to 50 overlapping accounts',
        tooltipNewPricing:
          'Create single partner Lists to view up to 50 overlapping accounts',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'Preview access to Advanced Account Mapping',
        tooltip:
          'Use advanced ecosystem filters to analyze overlaps across all partners',
      },
      {
        text: '3 %s',
        injection: 'Standard Populations',
        tooltip:
          'Standard populations include Customers, Prospects, and Open Opportunities',
      },
      {
        newPricingOnly: true,
        text: `${ODP_LIMIT_DEFAULTS_BY_TIER.free} %s`,
        injection: 'Open Data Partner',
        tooltip:
          'Find shared accounts with virtual partners built with always-fresh, third-party customer data and market signals',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'MCP Server',
        isBeta: true,
        isMcpServer: true,
        tooltip: 'Pull live ecosystem data into your AI tools',
        tooltipCreditSuffix: ' — consumes Crossbeam Credits',
      },
      {
        text: 'Preview access to %s',
        injection: 'Deal Navigator',
        tooltip:
          'Help your sales team identify high-quality, in-market opportunities',
        textNewPricing: 'Preview access to %s',
        injectionNewPricing: 'Deal Navigator and Pipeline Generation',
        tooltipNewPricing:
          'Help your sales team identify high-quality, in-market opportunities and prospects',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'Static and Dynamic Shared Lists by invitation',
        tooltip:
          'Strategize on overlapping accounts in real-time with teams and partners',
      },
      {
        text: '1 %s',
        injection: 'Offline Partner',
        tooltip: 'Account map with partners not active on Crossbeam yet',
      },
      {
        text: '%s',
        injection: 'Standard access controls',
        tooltip:
          'Leverage pre-defined user roles and permissions for your team',
      },
    ],
  },
  connector: {
    description:
      'For growing teams ready to turn ecosystem insights into action',
    features: [
      {
        text: 'Plan smarter with %s',
        injection: 'Advanced Account Mapping',
        tooltip:
          'Streamline advanced reporting with partner tags, pipeline insights, and ecosystem data',
        cta: [
          ACCOUNT_MAPPING_CTAS.REPORTS,
          ACCOUNT_MAPPING_CTAS.SAVED_REPORTS_ACCESS,
          ACCOUNT_MAPPING_CTAS.DO_MORE_WITH_REPORTS,
          ACCOUNT_MAPPING_CTAS.REPORT_FOLDERS,
          ACCOUNT_MAPPING_CTAS[POTENTIAL_REVENUE_TYPE],
          ACCOUNT_MAPPING_CTAS[`${POTENTIAL_REVENUE_TYPE}_COLUMNS`],
          ACCOUNT_MAPPING_CTAS[PARTNER_TAGS_TYPE],
          ACCOUNT_MAPPING_CTAS[ALL_PARTNERS_TYPE],
          ACCOUNT_MAPPING_CTAS[CUSTOM_TYPE],
          PARTNER_CTAS.POTENTIAL_REVENUE,
        ],
      },
      {
        text: '%s in CRMs, Chrome',
        injection: 'Crossbeam Copilot',
        tooltip:
          'Copilot delivers actionable partner insights directly into your existing sales tools',
        cta: [
          INTEGRATIONS_CTAS.COPILOT_FOR_SALESFORCE,
          INTEGRATIONS_CTAS.COPILOT_FOR_HUBSPOT,
          INTEGRATIONS_CTAS.COPILOT_FOR_CHROME,
          INTEGRATIONS_CTAS.COPILOT_FOR_OUTREACH,
        ],
      },
      {
        text: 'Full access to %s',
        injection: 'Crossbeam for Sales',
        tooltip:
          'Empower GTM teams to leverage ecosystem data with tools like Copilot, co-selling templates, and more',
      },
      {
        text: 'Export up to 5,000 %s',
        injection: 'unique records',
        tooltip:
          'Unlimited exports until you reach 5,000 unique records exported from reports or integrations',
        cta: [ACCOUNT_MAPPING_CTAS.RECORD_EXPORT_LIMIT],
      },
      {
        text: 'Discover %s with partners',
        injection: 'Greenfield Accounts',
        tooltip:
          'Identify new prospects and discover non-overlapping accounts with your partners',
        cta: [ACCOUNT_MAPPING_CTAS[GREENFIELD_TYPE]],
      },
      {
        text: 'Segment data with 3 %s',
        injection: 'Custom Populations',
        tooltip:
          'Refine your data segmentation to precisely target specific accounts',
        cta: [POPULATION_CTAS.CUSTOM_POPULATIONS],
      },
      {
        text: 'Unlimited %s',
        injection: 'Offline Partners',
        tooltip:
          'Account map with partners that are not active on Crossbeam yet',
        cta: [PARTNER_CTAS.OFFLINE_PARTNERS],
      },
    ],
    features_v5: [
      {
        text: 'Purchase unlimited Full Access seats',
        textNewPricing: 'Purchase additional Full Access seats',
      },
      {
        text: '%s',
        injection: '5,000 unique record exports',
        tooltip:
          'Get your data where you need it with record exports from reports and integrations',
        tooltipNewPricing:
          'Get your data where you need it with unique records exports into external data destinations',
      },
      {
        newPricingOnly: true,
        text: 'Full access to %s',
        injection: 'Single partner Account Mapping',
        tooltip:
          'Create single partner Lists and view all overlapping accounts',
      },
      {
        text: '%s',
        injection: 'Advanced Account Mapping',
        tooltip:
          'Use advanced ecosystem filters to analyze overlaps across all partners and save focused, actionable Lists',
        textNewPricing: 'Full access to %s',
      },
      {
        text: '3 %s',
        injection: 'Custom Populations',
        tooltip:
          'Organize and segment target accounts based on parameters you set',
      },
      {
        text: `${ODP_LIMIT_DEFAULTS_BY_TIER.connector} %s`,
        injection: 'Open Data Partner',
        tooltip:
          "Find shared accounts with companies that aren't on Crossbeam yet — and turn them into co-sell opportunities",
        icon: 'lock',
        isPlatformFeature: true,
        tooltipNewPricing:
          'Find shared accounts with virtual partners built with always-fresh, third-party customer data and market signals',
      },
      {
        text: '%s',
        injection: 'MCP Server',
        isBeta: true,
        isMcpServer: true,
        tooltip: 'Pull live ecosystem data into your AI tools',
        tooltipCreditSuffix: ' — consumes Crossbeam Credits',
        icon: 'lock',
        isPlatformFeature: true,
      },
      {
        text: 'Unlimited %s',
        injection: 'Offline Partners',
        tooltip: 'Account map with partners not active on Crossbeam yet',
      },
      {
        text: 'Full Access to %s',
        injection: 'Deal Navigator',
        tooltip:
          'Help your sales team identify high-quality, in-market opportunities',
        textNewPricing: 'Full access to %s',
        injectionNewPricing: 'Deal Navigator and Pipeline Generation',
        tooltipNewPricing:
          'Help your sales team identify high-quality, in-market opportunities and prospects',
      },
      {
        text: '%s',
        injection: 'Co-sell workflows',
        tooltip:
          'Keep co-sell motions moving with automated workflows and configurable messaging templates',
        injectionNewPricing: 'Co-sell Workflows',
      },
      {
        text: '%s sharing and reporting',
        injection: 'Greenfield',
        tooltip: 'Report on non-overlapping accounts with a partner',
      },
      {
        newPricingOnly: true,
        text: 'Full access to %s',
        injection: 'Static and Dynamic Shared Lists',
        tooltip:
          'Create overlap Lists and share with partners for live collaboration on co-selling and co-marketing initiatives',
      },
      {
        text: `${CREDIT_LIMIT_DEFAULTS_BY_TIER.connector} %s`,
        injection: 'MCP Credits',
        tooltip: `Spend ${CREDIT_LIMIT_DEFAULTS_BY_TIER.connector} credits a year on enriching records and running AI queries, refreshed at your renewal`,
        icon: 'lock',
        isPlatformFeature: true,
        hiddenUnderNewPricing: true,
      },
      {
        text: '%s',
        injection: 'AI Automations',
        tooltip:
          'Let AI automations work your ecosystem data for you, surfacing the overlaps and co-sell moves worth acting on',
        icon: 'lock',
        isPlatformFeature: true,
        hiddenUnderNewPricing: true,
      },
    ],
    featuresLongDescription: [
      {
        text: 'Accelerate deal cycles with smarter, %s',
        injection: 'advanced account mapping',
        tooltip:
          'Streamline advanced reporting with partner tags, pipeline insights, and ecosystem data',
      },
      {
        text: 'Collaborate effortlessly to keep you and your partners aligned on opportunities with %s',
        injection: 'account lists',
        tooltip:
          'Create overlap reports and share with partners for live collaboration on co-selling and co-marketing initiatives',
      },
      {
        text: 'Bring ecosystem insights directly to sellers with %s in CRMs and Chrome',
        injection: 'Crossbeam Copilot',
        tooltip:
          'Copilot delivers actionable partner insights directly into your existing sales tools',
      },
      {
        text: 'Get ecosystem data where your GTM teams need with %s',
        injection: '5,000 unique record exports',
        tooltip:
          'Unlimited exports until you reach 5,000 unique records exported from reports or integrations',
      },
      {
        text: 'Get sharper targeting with 3 additional Custom Populations to segment and compare ecosystem data',
      },
      {
        text: 'Identify new prospects and discover non-overlapping accounts with %s',
        injection: 'Greenfield Accounts',
        tooltip:
          'Identify new prospects and discover non-overlapping accounts with your partners',
      },
      {
        text: 'Maximize the value of your network and uncover offline revenue potential with unlimited Offline Partners',
      },
    ],
  },
  supernode: {
    description:
      'For enterprise teams ready to scale their ecosystems with advanced security, admin controls, and unlimited integrations',
    descriptionNewPricing:
      'For teams ready to scale and operationalize Crossbeam across their GTM org',
    features: [
      {
        text: '%s & SAML SSO',
        injection: 'Audit Logs',
        tooltip:
          'Monitor and audit your account as historical changes occur within your workspace',
      },
      {
        text: '%s for partner-driven revenue',
        injection: 'Attribution',
        tooltip:
          'Mark sourced and influenced from your partners right in Crossbeam',
      },
      {
        text: 'Unlimited integrations',
      },
      {
        text: '%s',
        injection: 'Custom record exports for teams',
        tooltip:
          'Unlimited record exports up to your team’s agreed unique record limit',
      },
      {
        text: 'Unlimited %s',
        injection: 'Custom Populations',
        tooltip:
          'Refine your data segmentation to precisely target specific accounts',
      },
      {
        text: '%s for Gong and Outreach',
        injection: 'Crossbeam Copilot',
        tooltip:
          'Copilot delivers actionable partner insights directly into your existing sales tools',
      },
      {
        text: '%s for Salesforce, HubSpot',
        injection: 'Custom Object integrations',
        tooltip:
          'Unlock custom object integrations with Salesforce, Snowflake, HubSpot and Microsoft Dynamics',
      },
      {
        text: '%s',
        injection: 'Enterprise-grade roles and permissions',
        tooltip:
          'Customize permission sets to control how your team accesses and interacts with data',
      },
      {
        text: 'Dedicated Customer Success Manager',
      },
      {
        text: 'Tailored Ecosystem Enablement for sales',
      },
    ],
    features_v5: [
      {
        text: '%s',
        injection: 'Flexible limits on record exports',
        tooltip: 'Flexible record exports limits based on your team’s needs',
        injectionNewPricing: '25,000 unique record exports',
        tooltipNewPricing:
          'Get your data where you need it with unique records exports into external data destinations',
      },
      {
        text: '%s',
        injection: 'Purchase unlimited Sales seats',
        tooltip:
          'Sales seats have access to all functionality within Copilot and Deal Navigator',
        tooltipNewPricing:
          'Sales seats have access to all functionality within Copilot, Deal Navigator, and Pipeline Generation, plus access to leverage Crossbeam MCP',
      },
      {
        text: '%s, including custom CRM objects',
        injection: 'Unlimited integrations',
        tooltip:
          'Send partner data everywhere with unlimited integrations, including Salesforce, HubSpot, Microsoft, Snowflake and more',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'REST API',
        tooltip: 'Read and write enabled',
      },
      {
        text: '%s',
        injection: 'Unlimited Custom Populations',
        tooltip:
          'Organize and segment target accounts based on parameters you set',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: '3 Open Data Partners',
        tooltip:
          'Find shared accounts with virtual partners built with always-fresh, third-party customer data and market signals',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'Product Line Support',
        tooltip:
          'Sync, share, filter, and segment data by product line. Includes product data for Open Data Partners.',
      },
      {
        text: '%s',
        injection: 'Performance Dashboard',
        tooltip:
          'Measure the revenue impact of your ecosystem and identify engagement gaps',
      },
      {
        text: '%s',
        injection: 'Revenue Attribution',
        tooltip:
          'Mark sourced and influenced from your partners right in Crossbeam',
      },
      {
        text: '%s',
        injection: 'Custom access controls',
        tooltip:
          'Customize roles and permission sets to control how your team accesses and interacts with data',
      },
      {
        text: '%s',
        injection: 'Audit Logs and SAML SSO',
        tooltip:
          'Monitor and audit your account as historical changes occur within your workspace',
      },
      {
        text: 'Dedicated Customer Success Manager',
      },
      {
        text: '%s',
        injection: 'Account Mapping Pass',
        tooltip:
          'Provides Free plan partners with full access to their overlaps with Supernode and Enterprise accounts for better collaboration',
      },
    ],
    featuresLongDescription: [
      {
        text: "Access %s tailored to your team's needs",
        injection: 'custom record exports',
        tooltip:
          'Unlimited record exports up to your team’s agreed unique record limit',
        icon: 'report',
      },
      {
        text: 'Unlock unlimited Custom Populations to analyze and compare ecosystem data',
        icon: 'population',
      },
      {
        text: 'Equip sales teams with %s in apps like Gong and Outreach',
        injection: 'Crossbeam Copilot',
        tooltip:
          'Copilot delivers actionable partner insights directly into your existing sales tools',
        icon: 'sales',
      },
      {
        text: 'Enrich views and create custom reports using ecosystem data via Custom Object integrations',
        icon: 'integration',
      },
      {
        text: 'Measure ecosystem impact with %s for sourced and influenced revenue',
        injection: 'attribution',
        tooltip:
          'Mark sourced and influenced from your partners right in Crossbeam',
        icon: 'measure',
      },
      {
        text: 'Ensure data security and compliance with Audit Logs',
        icon: 'lock',
      },
      {
        text: 'Receive expert onboarding and support with a dedicated Customer Success Manager',
        icon: 'person',
      },
      {
        text: 'Empower sales teams with tailored Ecoystem Enablement',
        icon: 'get-started',
      },
    ],
  },
  enterprise: {
    description:
      'For large organizations navigating complex partnerships, global teams, and large-scale collaboration',
    features_v5: [
      {
        text: '%s',
        injection: 'Starts with 100,000 unique record exports',
        tooltip:
          'Get your data where you need it with record exports from reports and integrations',
        tooltipNewPricing:
          'Get your data where you need it with unique records exports into external data destinations',
      },
      {
        text: 'Advanced CRM object support',
      },
      {
        newPricingOnly: true,
        text: 'Access to webhooks',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: '10 Open Data Partners',
        tooltip:
          'Find shared accounts with virtual partners built with always-fresh, third-party customer data and market signals',
      },
      {
        text: '%s',
        injection: 'Advanced access controls',
        tooltip:
          'Set partner visibility by role or user, ensuring users see only the partner data they need—improving security and collaboration',
      },
      {
        newPricingOnly: true,
        text: '%s',
        injection: 'SCIM user management',
        tooltip:
          'Automate user provisioning and deprovisioning between your identity provider and Crossbeam',
      },
      {
        text: '%s',
        injection: 'Custom support and services',
        tooltip:
          'Ensure your team has the proper training with customizable support and education',
      },
      {
        text: '%s',
        injection: 'White-glove ecosystem onboarding',
        tooltip:
          'Need to get your partners onboarded to Crossbeam? We’ll do the heavy lifting for you',
      },
    ],
  },
};
